import React, { useEffect, useState } from "react";
import {
  getContentCategoriesList,
  deleteContentCategory,
} from "../../../services/contentCategoriesService";
import { paginateData, calculateTotalPages } from "../../../assets/js/script";
import TableHeader from "../../Common/TableComponent/TableHeader";
import TableToolbar from "../../Common/TableComponent/TableToolbar";
import TablesRow from "../../Common/TableComponent/TablesRow";
import { renderStatusBadge } from "../../Common/TableComponent/tableBadges";
import { Pagination } from "../../Common/TableComponent/Pagination";
import { AddContentCategory } from "./AddContentCategory";
import { Loading } from "../../Common/OtherElements/Loading";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { confirmDelete } from "../../Common/OtherElements/confirmDeleteClone";
import { TableDataStatusError } from "../../Common/OtherElements/TableDataStatusError";
import { handleErrors } from "../../../utils/errorHandler";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";

const getCategoryId = (item) => item.id ?? item.Id;

const getCategoryName = (item) =>
  item.categoryName || item.CategoryName || "-";

const getCategorySlug = (item) =>
  item.categorySlug || item.CategorySlug || "-";

const getDisplayOrder = (item) =>
  item.displayOrder ?? item.DisplayOrder ?? "-";

const isPremiumCategory = (item) => {
  const value = item.isPremium ?? item.IsPremium;
  return value === true || value === "true" || value === 1;
};

const renderPremiumBadge = (item) => {
  const isPremium = isPremiumCategory(item);
  return (
    <span className={`badge badge-soft-${isPremium ? "warning" : "secondary"}`}>
      {isPremium ? "Premium" : "Standard"}
    </span>
  );
};

export const ManageContentCategoriesContent = () => {
  const [pageAccessDetails, setPageAccessDetails] = useState({});
  const pageLevelAccessUrl = "content-categories";
  const navigate = useNavigate();
  const { pageAccessData } = usePageLevelAccess(pageLevelAccessUrl);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [categoriesList, setCategoriesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.addAccess && !pageAccessData.viewAccess) {
        navigate("/404-error-page");
      } else {
        setPageAccessDetails(pageAccessData);
      }
    }
  }, [pageAccessData, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getContentCategoriesList();
      const list = response.data?.result || response.data || [];
      setCategoriesList(Array.isArray(list) ? list : []);
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredData = categoriesList.filter((item) => {
    const name = getCategoryName(item).toLowerCase();
    const slug = getCategorySlug(item).toLowerCase();
    const query = searchQuery.toLowerCase();
    return name.includes(query) || slug.includes(query);
  });

  const currentData = paginateData(filteredData, currentPage, entriesPerPage);
  const totalPages = calculateTotalPages(filteredData.length, entriesPerPage);

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleDelete = async (categoryId) => {
    const confirmed = await confirmDelete("Content Category");
    if (confirmed) {
      try {
        await deleteContentCategory(categoryId);
        setCategoriesList((prev) =>
          prev.filter((item) => getCategoryId(item) !== categoryId)
        );
        Swal.fire(
          "Deleted!",
          "The content category has been deleted successfully.",
          "success"
        );
        fetchData();
      } catch (error) {
        handleErrors(error);
      }
    }
  };

  return (
    <>
      {pageAccessDetails.addAccess ? (
        <AddContentCategory
          editMode={editMode}
          initialData={selectedCategory ?? {}}
          onSuccess={fetchData}
          setSelectedCategory={setSelectedCategory}
          setEditMode={setEditMode}
        />
      ) : (
        ""
      )}

      {pageAccessDetails.viewAccess ? (
        <div className="row">
          <div className="col-xxl-12">
            <div className="card mt-xxl-n5">
              <div className="card-header">
                <h5 className="mb-sm-2 mt-sm-2">Manage Content Categories</h5>
              </div>
              <div className="card-body manage-amenity-master-card-body">
                <TableToolbar
                  entriesPerPage={entriesPerPage}
                  onEntriesChange={handleEntriesChange}
                  searchValue={searchQuery}
                  searchPlaceholder="Search..."
                  onSearchChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                />

                {loading ? (
                  <Loading />
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle table-bordered">
                      <TableHeader
                        columns={[
                          "Sl No",
                          "Category Name",
                          "Category Slug",
                          "Premium",
                          "Display Order",
                          "Status",
                          "Action",
                        ]}
                      />
                      <tbody className="manage-page-group-table-values">
                        {currentData.length === 0 ? (
                          <TableDataStatusError colspan="7" />
                        ) : (
                          currentData.map((item, index) => (
                            <TablesRow
                              key={getCategoryId(item)}
                              rowData={{
                                slNo:
                                  (currentPage - 1) * entriesPerPage +
                                  index +
                                  1,
                                categoryName: getCategoryName(item),
                                categorySlug: getCategorySlug(item),
                                premium: renderPremiumBadge(item),
                                displayOrder: getDisplayOrder(item),
                                status: renderStatusBadge(item),
                              }}
                              columns={[
                                "slNo",
                                "categoryName",
                                "categorySlug",
                                "premium",
                                "displayOrder",
                                "status",
                              ]}
                              hideIcons={false}
                              onEdit={() => {
                                setSelectedCategory(item);
                                setEditMode(true);
                                window.scrollTo({ top: 0, behavior: "smooth" });
                              }}
                              onDelete={() => handleDelete(getCategoryId(item))}
                              pageLevelAccessData={pageAccessDetails}
                            />
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalEntries={filteredData.length}
                  entriesPerPage={entriesPerPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
    </>
  );
};
